import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Col, Row, Alert, Spin } from "antd";
import { RobotOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { RootState } from "../../shared/types/common";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { allPermissions } from "../../shared/constants/constants";
import {
  getAiConfig,
  updateAiConfig,
} from "../../core/store/reducers/candidate-screening.slice";

const PROVIDER_OPTIONS = [
  { value: "OPENAI", label: "OpenAI" },
  { value: "AZURE_OPENAI", label: "Azure OpenAI" },
  { value: "CLAUDE", label: "Claude (Anthropic)" },
];

const AiConfigPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { aiConfig, loadingAiConfig } = useSelector(
    (state: RootState) => state.candidateScreening
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [saving, setSaving] = useState(false);

  // Full access (no role-level gating configured for this module yet).
  const canEdit = accessAllowed(myPermissions, allPermissions.AICONFIG_CREATE);

  useEffect(() => {
    dispatch(getAiConfig());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (aiConfig) {
      form.setFieldsValue({
        provider: aiConfig?.provider,
        endpoint: aiConfig?.endpoint,
        model: aiConfig?.model,
        embeddingModel: aiConfig?.embeddingModel,
        temperature: aiConfig?.temperature ?? 0,
        maxTokens: aiConfig?.maxTokens ?? 1024,
        promptVersion: aiConfig?.promptVersion,
        active: aiConfig?.active ?? true,
      });
    }
  }, [form, aiConfig]);

  const onFinish = (values: any) => {
    setSaving(true);
    const payload = {
      provider: values?.provider,
      endpoint: values?.endpoint,
      model: values?.model,
      embeddingModel: values?.embeddingModel,
      temperature: Number(values?.temperature ?? 0),
      maxTokens: Number(values?.maxTokens ?? 0),
      promptVersion: values?.promptVersion,
      active: !!values?.active,
    };
    dispatch(updateAiConfig(payload)).then((val: any) => {
      setSaving(false);
      if (val?.payload?.data) {
        showNotification("AI configuration saved.", "success");
        dispatch(getAiConfig());
      }
    });
  };

  const formProps = {
    form: form,
    name: "ai-config",
    initialValues: {
      provider: "OPENAI",
      temperature: 0,
      maxTokens: 1024,
      active: true,
    },
    formElements: [
      {
        name: "provider",
        label: "Provider",
        placeholder: "Select provider",
        type: "Select",
        colSpan: 12,
        options: PROVIDER_OPTIONS,
        disabled: !canEdit,
        rules: [{ required: true, message: "Please select a provider" }],
      },
      {
        name: "active",
        label: "Active",
        type: "Switch",
        colSpan: 12,
        valuePropName: "checked",
        disabled: !canEdit,
      },
      {
        name: "endpoint",
        label: "API Endpoint",
        placeholder: "https://api.openai.com/v1  (or Azure/Claude endpoint)",
        type: "Input",
        colSpan: 24,
        allowClear: true,
        disabled: !canEdit,
        rules: [{ required: true, message: "Please enter the endpoint" }],
      },
      {
        name: "model",
        label: "Scoring Model",
        placeholder: "e.g. gpt-4o-mini / claude-... ",
        type: "Input",
        colSpan: 12,
        allowClear: true,
        disabled: !canEdit,
        rules: [{ required: true, message: "Please enter the model" }],
      },
      {
        name: "embeddingModel",
        label: "Embedding Model",
        placeholder: "e.g. text-embedding-3-small",
        type: "Input",
        colSpan: 12,
        allowClear: true,
        disabled: !canEdit,
      },
      {
        name: "temperature",
        label: "Temperature (0 = deterministic)",
        placeholder: "0",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 2,
        disabled: !canEdit,
      },
      {
        name: "maxTokens",
        label: "Max Tokens",
        placeholder: "1024",
        type: "InputNumber",
        colSpan: 8,
        min: 1,
        disabled: !canEdit,
      },
      {
        name: "promptVersion",
        label: "Prompt Version",
        placeholder: "e.g. v1",
        type: "Input",
        colSpan: 8,
        allowClear: true,
        disabled: true,
      },
    ],
    extraButtons: canEdit
      ? [
          {
            htmlType: "submit",
            colSpan: 6,
            loading: saving,
            submitButtonText: "Save Configuration",
            submitButtonType: "primary",
          },
        ]
      : [],
    onFinish: onFinish,
    onFinishFailed: () => {
      return;
    },
  };

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <RobotOutlined />
              </div>
              <h1 className="app-page-title">AI Configuration</h1>
            </div>
            <p className="app-page-subtitle">
              Provider, models, and deterministic AI settings
            </p>
          </Col>
        </Row>
      </div>

      <div className="mx-6" style={{ padding: "0 24px" }}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="API keys are never stored here."
          description="The provider API key is read from a server environment variable at request time (plan.md §6, §10). This screen only configures the endpoint, models, and scoring parameters."
        />
        <Card>
          {loadingAiConfig ? (
            <div style={{ textAlign: "center", padding: 32 }}>
              <Spin />
            </div>
          ) : (
            <GenericFormComponent {...formProps} />
          )}
        </Card>
      </div>
    </div>
  );
};

export default AiConfigPage;

